import React, { useEffect, useState, useRef } from 'react';
import { cn } from '../../utils/cn';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, PackageCheck, HandMetal, Truck, CircleCheckBig, ShoppingBag, UserCheck, PackageX, Ban, Slice, Hash, Lock, RotateCcw, Info, ArrowUpRight, ArrowDownRight } from 'lucide-react';
interface FlatStatusItem {
  label: string;
  code: string;
  count: number;
  prevCount: number;
  icon: React.ElementType;
  iconColor: string;
  description: string;
  borderColor: string;
  category: string;
}
const ALL_STATUSES: FlatStatusItem[] = [
// Active
{
  label: 'Accepted',
  code: 'ACCEPTED_BY_PROVIDER',
  count: 145,
  prevCount: 132,
  icon: CheckCircle2,
  iconColor: 'text-primary-500',
  description: 'Orders accepted by the pharmacy branch and queued for processing.',
  borderColor: 'border-l-primary-500',
  category: 'Active'
}, {
  label: 'Dispensed',
  code: 'DISPENSED',
  count: 89,
  prevCount: 78,
  icon: PackageCheck,
  iconColor: 'text-primary-500',
  description: 'Medications have been prepared and dispensed by the pharmacist.',
  borderColor: 'border-l-primary-500',
  category: 'Active'
}, {
  label: 'Ready',
  code: 'READY_FOR_CUSTOMER_PICKUP',
  count: 34,
  prevCount: 40,
  icon: HandMetal,
  iconColor: 'text-green-500',
  description: 'Order is ready and waiting for the patient to pick up at the branch.',
  borderColor: 'border-l-primary-500',
  category: 'Active'
}, {
  label: 'Out Delivery',
  code: 'OUT_FOR_DELIVERY',
  count: 56,
  prevCount: 48,
  icon: Truck,
  iconColor: 'text-sky-500',
  description: 'Order has been handed to the delivery driver and is on its way to the patient.',
  borderColor: 'border-l-primary-500',
  category: 'Active'
}, {
  label: 'Delivered',
  code: 'DELIVERED',
  count: 890,
  prevCount: 820,
  icon: CircleCheckBig,
  iconColor: 'text-primary-600',
  description: "Order successfully delivered to the patient's address.",
  borderColor: 'border-l-primary-500',
  category: 'Active'
}, {
  label: 'Picked',
  code: 'PICKED_BY_CONSUMER',
  count: 430,
  prevCount: 395,
  icon: ShoppingBag,
  iconColor: 'text-primary-600',
  description: 'Patient has picked up the order from the pharmacy branch.',
  borderColor: 'border-l-primary-500',
  category: 'Active'
},
// Pending
{
  label: 'Waiting Patient',
  code: 'WAITING_PATIENT_CONFIRMATION',
  count: 23,
  prevCount: 28,
  icon: UserCheck,
  iconColor: 'text-amber-600',
  description: 'Order is waiting for the patient to confirm or approve the prescription.',
  borderColor: 'border-l-amber-500',
  category: 'Pending'
},
// Cancellation
{
  label: 'Can - No Stock',
  code: 'CANCELED_NO_STOCK',
  count: 15,
  prevCount: 18,
  icon: PackageX,
  iconColor: 'text-orange-500',
  description: 'Order was canceled because the item is not available in any branch.',
  borderColor: 'border-l-orange-500',
  category: 'Cancellation'
}, {
  label: 'Can - Partial',
  code: 'CANCELED_PARTIAL',
  count: 7,
  prevCount: 9,
  icon: Slice,
  iconColor: 'text-orange-400',
  description: 'Order was canceled because only part of the prescription could be fulfilled.',
  borderColor: 'border-l-orange-500',
  category: 'Cancellation'
}, {
  label: 'Can - Qty Issue',
  code: 'CANCELED_QTY',
  count: 4,
  prevCount: 3,
  icon: Hash,
  iconColor: 'text-orange-500',
  description: 'Order was canceled due to a quantity mismatch or issue.',
  borderColor: 'border-l-orange-500',
  category: 'Cancellation'
}, {
  label: 'Can - Closed',
  code: 'CANCELED_CLOSED',
  count: 1,
  prevCount: 2,
  icon: Lock,
  iconColor: 'text-orange-400',
  description: 'Order was canceled because the assigned branch was closed.',
  borderColor: 'border-l-orange-500',
  category: 'Cancellation'
},
// Returns / Issues
{
  label: 'Returned',
  code: 'RETURNED',
  count: 22,
  prevCount: 19,
  icon: RotateCcw,
  iconColor: 'text-purple-500',
  description: 'The order was returned back to the pharmacy after delivery attempt.',
  borderColor: 'border-l-purple-500',
  category: 'Returns'
}];
function StatusItemTooltip({
  description,
  label
}: {
  description: string;
  label: string;
}) {
  const [show, setShow] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!show) return;
    function handleClickOutside(e: MouseEvent) {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target as Node) && buttonRef.current && !buttonRef.current.contains(e.target as Node)) {
        setShow(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [show]);
  return <div className="relative inline-flex" data-id="element-99">
      <button ref={buttonRef} onClick={e => {
      e.stopPropagation();
      setShow(!show);
    }} className="text-slate-300 hover:text-primary-500 transition-colors" aria-label={`Info about ${label}`} data-id="element-100">
        <Info className="w-3 h-3" data-id="element-101" />
      </button>
      <AnimatePresence data-id="element-102">
        {show && <motion.div ref={tooltipRef} initial={{
        opacity: 0,
        y: 4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 4
      }} transition={{
        duration: 0.15
      }} className="absolute right-0 top-full mt-1.5 z-50 w-52 bg-slate-900 text-white text-[11px] rounded-lg p-2.5 shadow-lg border border-slate-700" data-id="element-103">
            <div className="absolute -top-1 right-2 w-2 h-2 bg-slate-900 border-l border-t border-slate-700 rotate-45" data-id="element-104" />
            <p className="leading-relaxed" data-id="element-105">{description}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function StatusBreakdown() {
  const totalOrders = ALL_STATUSES.reduce((sum, item) => sum + item.count, 0);
  return <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-id="element-106">
      {ALL_STATUSES.map(item => {
      const Icon = item.icon;
      const percentage = totalOrders > 0 ? (item.count / totalOrders * 100).toFixed(1) : '0.0';
      const change = item.prevCount > 0 ? Math.round((item.count - item.prevCount) / item.prevCount * 100) : 0;
      const isPositive = change >= 0;
      return <div key={item.code} className={cn('bg-white rounded-lg border border-slate-200 shadow-sm border-l-4 p-4 hover:shadow-md transition-all', item.borderColor)} data-id="element-107">
            <div className="flex items-center gap-2 mb-3" data-id="element-108">
              <div className={cn('flex-shrink-0', item.iconColor)} data-id="element-109">
                <Icon className="w-5 h-5" data-id="element-110" />
              </div>
              <div className="flex items-center gap-1.5 min-w-0" data-id="element-111">
                <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold truncate" title={item.code} data-id="element-112">
                  {item.label}
                </span>
                <StatusItemTooltip description={item.description} label={item.label} data-id="element-113" />
              </div>
            </div>
            <div className="flex items-baseline gap-2" data-id="element-114">
              <span className="text-2xl font-bold text-slate-900" data-id="element-115">
                {item.count}
              </span>
              <span className="text-xs font-medium text-slate-400" data-id="element-116">
                ({percentage}%)
              </span>
            </div>
            <div className="flex items-center justify-between mt-1.5" data-id="element-117">
              <p className="text-[10px] text-slate-400" data-id="element-118">{item.category}</p>
              {change !== 0 && <span className={cn('flex items-center text-[10px] font-medium', isPositive ? 'text-primary-600' : 'text-red-500')} data-id="element-119">
                  {isPositive ? <ArrowUpRight className="w-2.5 h-2.5 mr-0.5" data-id="element-120" /> : <ArrowDownRight className="w-2.5 h-2.5 mr-0.5" data-id="element-121" />}
                  {Math.abs(change)}%
                  <span className="ml-1 text-slate-400 font-normal" data-id="element-122">
                    vs last
                  </span>
                </span>}
            </div>
          </div>;
    })}
    </div>;
}